/**
 * Runtime configuration — the one place that reads `window.__ENV__`.
 *
 * Vite bakes `import.meta.env` into the bundle at build time, so a value that
 * differs per environment (the API address, the Keycloak realm) cannot come
 * from there: one image would carry one environment's addresses forever. The
 * addresses are therefore served at runtime from `/config.js`, which nginx
 * reads from a mounted ConfigMap — one image, every environment.
 *
 * The bundled `public/config.js` is a placeholder that a deployment replaces.
 * If it is still the placeholder in a production build, this module throws
 * instead of falling back to a default. That is deliberate: a fallback would
 * silently point a staging deployment at production, which is the exact
 * failure this mechanism exists to prevent, and nothing about it would look
 * broken.
 */

export interface RuntimeEnv {
	apiUrl: string;
	keycloakUrl: string;
	keycloakRealm: string;
	keycloakClientId: string;
	/** Optional: falls back to the browser origin (Traefik routes /socket.io). */
	socketUrl?: string;
	/**
	 * Optional: error reporting, off when absent.
	 *
	 * Deliberately NOT in REQUIRED_KEYS, unlike everything above it. A missing
	 * API address must stop the app, because the alternative is talking to the
	 * wrong environment; a missing DSN must not, because the alternative is a
	 * platform nobody can sign in to because its error reporter has no address.
	 * The two have opposite failure directions and the same shape.
	 */
	sentryDsn?: string;
	/**
	 * Optional: which deployment this is, as Sentry labels it.
	 *
	 * Named here and never taken from `import.meta.env.MODE`, which is
	 * "production" for any Vite production build and would therefore file every
	 * staging event under production. One image serves every environment
	 * (besluit 0004), so the bundle cannot know which one it is running in.
	 */
	environment?: string;
}

/** Marker written by the placeholder shipped in the image; see public/config.js. */
const PLACEHOLDER_SOURCE = 'bundled-placeholder';

interface RuntimeEnvCarrier extends Partial<RuntimeEnv> {
	source?: string;
}

declare global {
	interface Window {
		// Named by the convention for a runtime-injected global, deliberately
		// unlike anything the bundle declares itself.
		// eslint-disable-next-line @typescript-eslint/naming-convention
		__ENV__?: RuntimeEnvCarrier | null;
	}
}

const REQUIRED_KEYS = [
	'apiUrl',
	'keycloakUrl',
	'keycloakRealm',
	'keycloakClientId',
] as const;

/**
 * Local defaults, used only by a development build.
 *
 * The API address defaults to localhost, which is where a local stack runs.
 * The Keycloak addresses deliberately have NO default: naming one means a
 * laptop with the bypass switched off signs in against whichever realm was
 * written here, and the obvious value to write is production. That is the same
 * silent-fallback-to-production this whole module exists to prevent, and it is
 * not less bad for happening in a development build — it is a real session
 * against a real realm. Say which realm you mean, in VITE_KEYCLOAK_*.
 */
const developmentEnv = (): RuntimeEnv => ({
	apiUrl: import.meta.env.VITE_API_URL ?? 'http://localhost:8000',
	keycloakUrl: import.meta.env.VITE_KEYCLOAK_URL ?? '',
	keycloakRealm: import.meta.env.VITE_KEYCLOAK_REALM ?? '',
	keycloakClientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? '',
	socketUrl: import.meta.env.VITE_SOCKET_URL,
	// Unset on a laptop, which is what keeps a development build silent. Set it
	// to verify the reporting path itself; events then arrive labelled
	// 'development' rather than mixed in with a real environment's.
	sentryDsn: import.meta.env.VITE_SENTRY_DSN,
	environment: 'development',
});

const fail = (reason: string): never => {
	throw new Error(
		`Runtime configuration missing: ${reason}. The application cannot start. ` +
			'/config.js should be served from a mounted ConfigMap and define ' +
			'window.__ENV__ with apiUrl, keycloakUrl, keycloakRealm and ' +
			'keycloakClientId. See docs/decisions/0004-runtime-config-for-the-spa.md.',
	);
};

const resolve = (): RuntimeEnv => {
	const carrier = typeof window === 'undefined' ? undefined : window.__ENV__;
	const isUsable =
		carrier != null && carrier.source !== PLACEHOLDER_SOURCE;

	if (!isUsable) {
		// A development build has no ConfigMap to mount, so local defaults are
		// the correct answer there and only there.
		if (import.meta.env.DEV) return developmentEnv();
		return fail(
			carrier == null
				? '/config.js did not define window.__ENV__'
				: '/config.js is still the placeholder shipped in the image',
		);
	}

	// Non-empty strings only. `!value` already catches undefined and '', but a
	// value of the wrong type (a number, an object from a hand-edited ConfigMap)
	// is truthy and would travel on to keycloak-js as a broken address.
	const missing = REQUIRED_KEYS.filter((key) => {
		const value = carrier[key];
		return typeof value !== 'string' || value.trim() === '';
	});
	if (missing.length > 0) {
		if (import.meta.env.DEV) return { ...developmentEnv(), ...carrier };
		return fail(`window.__ENV__ has no ${missing.join(', ')}`);
	}

	// The optional keys get the same type check the required ones just had, for
	// a reason the required ones do not have: a wrong type there fails loudly at
	// the first request, but @sentry/core's own DSN validation begins with
	// `if (!DEBUG_BUILD) return true` — so in a production bundle a numeric
	// sentryDsn builds a client with an undefined host and posts every event
	// into nothing, while isEnabled() reports true.
	const optional = { ...carrier } as RuntimeEnvCarrier;
	for (const key of ['sentryDsn', 'environment'] as const) {
		const value = optional[key];
		if (value !== undefined && (typeof value !== 'string' || value.trim() === '')) {
			delete optional[key];
		}
	}

	return optional as RuntimeEnv;
};

export const runtimeEnv: RuntimeEnv = resolve();
